import type { WidgetOptions } from "./config";
import type { WidgetEvent, WidgetPayload } from "./types";

export function renderLoading(element: HTMLElement, options?: WidgetOptions): void {
  replaceContent(element, widgetClass("lm-widget lm-widget--loading", options), [
    paragraph("Loading events...", "lm-widget__message"),
  ], options);
}

export function renderMissingKey(element: HTMLElement): void {
  replaceContent(element, "lm-widget lm-widget--error", [
    paragraph("This events widget is missing its public key.", "lm-widget__message"),
  ]);
}

export function renderCalendarUnavailable(element: HTMLElement, options?: WidgetOptions): void {
  replaceContent(element, widgetClass("lm-widget lm-widget--calendar lm-widget--calendar-unavailable", options), [
    header("Calendar view", "Calendar display is coming soon."),
  ], options);
}

export function renderError(element: HTMLElement, options?: WidgetOptions): void {
  replaceContent(element, widgetClass("lm-widget lm-widget--error", options), [
    paragraph("Events are unavailable right now.", "lm-widget__message"),
  ], options);
}

export function renderPayload(element: HTMLElement, payload: WidgetPayload, options: WidgetOptions): void {
  if (!payload.eligible) {
    renderMembershipRequired(element, payload, options);
    return;
  }

  if (payload.events.length === 0) {
    renderEmpty(element, payload, options);
    return;
  }

  if (options.view === "tiles") {
    renderTiles(element, payload, options);
  } else {
    renderList(element, payload, options);
  }
}

function renderTiles(element: HTMLElement, payload: WidgetPayload, options: WidgetOptions): void {
  const list = document.createElement("ul");
  list.className = "lm-widget__events";

  for (const event of payload.events) {
    list.append(renderEvent(event, options, payload.deployment?.timezone));
  }

  const children: Node[] = [list];
  const moreUrl = payload.moreUrl ?? payload.deployment?.eventsUrl ?? payload.member?.url;
  if (moreUrl) {
    children.push(actions(link(moreLinkText(payload), moreUrl, "lm-widget__more")));
  }

  const baseClass = options.columns
    ? `lm-widget lm-widget--tiles lm-widget--columns lm-widget--columns-${options.columns}`
    : "lm-widget lm-widget--tiles";

  replaceContent(element, widgetClass(baseClass, options), children, options);
}

function renderList(element: HTMLElement, payload: WidgetPayload, options: WidgetOptions): void {
  const list = document.createElement("ul");
  list.className = "lm-widget__events";

  for (const event of payload.events) {
    list.append(renderEvent(event, options, payload.deployment?.timezone));
  }

  const children: Node[] = [list];
  const moreUrl = payload.moreUrl ?? payload.deployment?.eventsUrl ?? payload.member?.url;
  if (moreUrl) {
    children.push(actions(link(moreLinkText(payload), moreUrl, "lm-widget__more")));
  }

  replaceContent(element, widgetClass("lm-widget lm-widget--list", options), children, options);
}

function renderEmpty(element: HTMLElement, payload: WidgetPayload, options?: WidgetOptions): void {
  const moreUrl = payload.moreUrl ?? payload.deployment?.eventsUrl ?? payload.member?.url;
  const children: Node[] = [
    paragraph("No upcoming public events are listed right now.", "lm-widget__message"),
  ];
  if (moreUrl) {
    children.push(actions(link(moreLinkText(payload), moreUrl, "lm-widget__more")));
  }
  replaceContent(element, widgetClass("lm-widget lm-widget--empty", options), children, options);
}

function renderMembershipRequired(element: HTMLElement, payload: WidgetPayload, options?: WidgetOptions): void {
  const message = payload.reason === "membership_required"
    ? `${payload.deployment?.name ?? "Loves Music"} membership is required to show events here.`
    : "Events are not available for this profile right now.";
  const children: Node[] = [paragraph(message, "lm-widget__message")];
  if (payload.cta) {
    children.push(actions(link(payload.cta.label, payload.cta.url, "lm-widget__cta")));
  }
  replaceContent(element, widgetClass("lm-widget lm-widget--membership-required", options), children, options);
}

function renderEvent(event: WidgetEvent, options: WidgetOptions, deploymentTimezone?: string): HTMLLIElement {
  const item = document.createElement("li");
  item.className = "lm-event";
  const title = event.title ?? event.name ?? "Untitled event";
  const candidates = eventImageCandidates(event);
  const hasImage = options.showImage && candidates.length > 0;
  if (hasImage) {
    item.classList.add("lm-event--with-image");
  }

  const body = document.createElement("div");
  body.className = "lm-event__body";

  if (options.showTitle) {
    const titleNode = event.url
      ? link(title, event.url, "lm-event__title")
      : span(title, "lm-event__title");
    body.append(titleNode);
  }

  const meta = document.createElement("div");
  meta.className = "lm-event__meta";
  meta.append(dateMetaRow(paragraph(formatEventDate(event, deploymentTimezone, options.showTimezone), "lm-event__date")));

  if (options.showVenue && event.venue?.name) {
    meta.append(metaRow("Venue", "venue", paragraphWithOptionalLink(event.venue.name, event.venue.url, "lm-event__venue")));
  }

  if (options.showArtists && event.artists?.length) {
    meta.append(metaRow("Musicians", "artists", artistList(event.artists, "lm-event__artists")));
  }

  body.append(meta);
  if (hasImage) {
    item.append(eventImage(candidates, title, event.url, () => item.classList.remove("lm-event--with-image")));
  }
  item.append(body);
  return item;
}

/** Accept new responses and older APIs containing only imageUrl. */
function eventImageCandidates(event: WidgetEvent): string[] {
  const raw = Array.isArray(event.imageCandidates) && event.imageCandidates.length
    ? event.imageCandidates : [event.imageUrl];
  const urls = raw.filter((url): url is string => {
    if (typeof url !== 'string') return false;
    try { const parsed = new URL(url); return parsed.protocol === 'https:' || parsed.protocol === 'http:'; }
    catch { return false; }
  }).map(url => url.trim());
  return Array.from(new Set(urls));
}

function eventImage(candidates: string[], title: string, eventUrl: string | undefined, exhausted: () => void): HTMLElement {
  const makeImage = () => {
    const image = document.createElement("img");
    image.className = "lm-event__image lm-event__image--loading";
    image.alt = "";
    image.loading = "lazy";
    image.decoding = "async";
    return image;
  };
  let currentImage = makeImage();
  let frame: HTMLElement = eventUrl ? eventImageLink(title, eventUrl, currentImage) : currentImage;
  const loadingClass = eventUrl ? "lm-event__image-link--loading" : "lm-event__image--loading";
  const loadedClass = eventUrl ? "lm-event__image-link--loaded" : "lm-event__image--loaded";
  const startAttempt = (index: number) => {
    const image = currentImage;
    let settled = false;
    frame.classList.remove(loadedClass);
    frame.classList.add(loadingClass);
    image.addEventListener("load", () => {
      if (currentImage !== image || settled) return;
      settled = true;
      image.classList.remove("lm-event__image--loading");
      image.classList.add("lm-event__image--loaded");
      frame.classList.remove(loadingClass);
      frame.classList.add(loadedClass);
    }, { once: true });
    image.addEventListener("error", () => {
      if (currentImage !== image || settled) return;
      settled = true;
      if (index + 1 === candidates.length) { frame.remove(); exhausted(); return; }
      const nextImage = makeImage();
      image.replaceWith(nextImage);
      currentImage = nextImage;
      if (!eventUrl) frame = nextImage;
      startAttempt(index + 1);
    }, { once: true });
    const url = candidates[index];
    if (!url) { frame.remove(); exhausted(); return; }
    image.src = url;
  };
  startAttempt(0);
  return frame;
}

function eventImageLink(title: string, eventUrl: string, image: HTMLImageElement): HTMLAnchorElement {
  const imageLink = document.createElement("a");
  imageLink.className = "lm-event__image-link";
  imageLink.href = eventUrl;
  openInNewTab(imageLink);
  imageLink.setAttribute("aria-label", title);
  imageLink.append(image);
  return imageLink;
}

function formatEventDate(event: WidgetEvent, deploymentTimezone?: string, showTimezone: boolean = false): string {
  const startTime = event.startTime ?? event.startDateTime ?? "";
  const date = new Date(startTime);
  if (Number.isNaN(date.getTime())) {
    return startTime;
  }
  const timezone = event.timezone ?? deploymentTimezone;
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(showTimezone ? { timeZoneName: "short" } : {}),
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(date);
}

function widgetClass(className: string, options?: WidgetOptions): string {
  return options?.scrollable ? `${className} lm-widget--scrollable` : className;
}

function moreLinkText(payload: WidgetPayload): string {
  return payload.deployment?.name ? `More at ${payload.deployment.name}` : "More events";
}

function header(title: string, subtitle?: string): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "lm-widget__header";
  const heading = document.createElement("h2");
  heading.className = "lm-widget__title";
  heading.textContent = title;
  wrapper.append(heading);
  if (subtitle) {
    wrapper.append(paragraph(subtitle, "lm-widget__subtitle"));
  }
  return wrapper;
}

function actions(child: Node): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "lm-widget__actions";
  wrapper.append(child);
  return wrapper;
}

function dateMetaRow(value: Node): HTMLElement {
  const row = document.createElement("div");
  row.className = "lm-event__meta-row lm-event__meta-row--date";
  row.append(value);
  return row;
}

function metaRow(label: string, kind: "venue" | "artists", value: Node): HTMLElement {
  const row = document.createElement("div");
  row.className = `lm-event__meta-row lm-event__meta-row--${kind}`;
  const labelNode = document.createElement("span");
  labelNode.className = "lm-event__meta-label";
  labelNode.textContent = label;
  row.append(labelNode, value);
  return row;
}

function artistList(artists: Array<{ name: string; url?: string }>, className: string): HTMLParagraphElement {
  const node = document.createElement("p");
  node.className = className;
  artists.forEach((artist, index) => {
    if (index > 0) {
      node.append(document.createTextNode(", "));
    }
    node.append(artist.url ? link(artist.name, artist.url, "") : document.createTextNode(artist.name));
  });
  return node;
}

function paragraph(text: string, className: string): HTMLParagraphElement {
  const node = document.createElement("p");
  node.className = className;
  node.textContent = text;
  return node;
}

function paragraphWithOptionalLink(text: string, href: string | undefined, className: string): HTMLParagraphElement {
  const node = document.createElement("p");
  node.className = className;
  node.append(href ? link(text, href, "") : document.createTextNode(text));
  return node;
}

function link(text: string, href: string, className: string): HTMLAnchorElement {
  const anchor = document.createElement("a");
  anchor.href = href;
  openInNewTab(anchor);
  if (className) {
    anchor.className = className;
  }
  anchor.textContent = text;
  return anchor;
}

function openInNewTab(anchor: HTMLAnchorElement): void {
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
}

function span(text: string, className: string): HTMLSpanElement {
  const node = document.createElement("span");
  node.className = className;
  node.textContent = text;
  return node;
}

function replaceContent(element: HTMLElement, className: string, children: Node[], options?: WidgetOptions): void {
  element.className = className;
  if (options?.scrollable) {
    element.style.setProperty("--lm-widget-scroll-height", `${options.maxHeight}px`);
  } else {
    element.style.removeProperty("--lm-widget-scroll-height");
  }
  if (options?.columns) {
    element.style.setProperty("--lm-widget-columns", String(options.columns));
  } else {
    element.style.removeProperty("--lm-widget-columns");
  }
  element.replaceChildren(...children);
}
